import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AddPasswordModal from './AddPasswordModal';
import NoteFieldWithContextMenu from './NoteFieldWithContextMenu';

export interface CreateTemplateModalResult {
  title: string;
  content: string;
}

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: CreateTemplateModalResult) => void;
  /** Edit modu: şablon id'si verilirse modal düzenleme ekranı olarak açılır */
  templateId?: string;
  initialTitle?: string;
  initialContent?: string;
  onUpdate?: (id: string, data: CreateTemplateModalResult) => void | Promise<void>;
  titleLabel?: string;
  titlePlaceholder?: string;
  contentLabel?: string;
  contentPlaceholder?: string;
  saveLabel?: string;
  cancelLabel?: string;
  modalTitle?: string;
  contentRequiredError?: string;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  isOpen,
  onClose,
  onSave,
  templateId,
  initialTitle = '',
  initialContent = '',
  onUpdate,
  titleLabel,
  titlePlaceholder,
  contentLabel,
  contentPlaceholder,
  saveLabel,
  cancelLabel,
  modalTitle,
  contentRequiredError,
}) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(templateId);
  const [templateTitle, setTemplateTitle] = useState('');
  const [content, setContent] = useState('');
  const [titleError, setTitleError] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode) {
        setTemplateTitle(initialTitle);
        setContent(initialContent);
      } else {
        setTemplateTitle('');
        setContent('');
      }
      setTitleError(null);
      setContentError(null);
    }
  }, [isOpen, isEditMode, initialTitle, initialContent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTitle = templateTitle.trim();
    const trimmedContent = content.trim();
    const titleErr = t('pages.passwords.templateModal.titleRequired', 'Başlık zorunludur.');
    const contentErr = contentRequiredError ?? t('pages.passwords.templateModal.fieldRequired', 'İçerik boş bırakılamaz.');
    let hasError = false;
    if (!trimmedTitle) {
      setTitleError(titleErr);
      hasError = true;
    } else {
      setTitleError(null);
    }
    if (!trimmedContent) {
      setContentError(contentErr);
      hasError = true;
    } else {
      setContentError(null);
    }
    if (hasError) return;
    setIsSaving(true);
    try {
      const data = { title: trimmedTitle, content: trimmedContent };
      if (isEditMode && templateId && onUpdate) {
        await onUpdate(templateId, data);
      } else if (onSave) {
        onSave(data);
      }
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AddPasswordModal
      isOpen={true}
      onClose={onClose}
      title={modalTitle ?? (isEditMode ? t('pages.passwords.templateModal.editTitle', 'Şablonu düzenle') : t('pages.passwords.templateModal.title', 'Şablon oluştur'))}
      maxWidth="max-w-7xl"
    >
      <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0">
        {/* Başlık alanı - EditPasswordForm ile aynı yapı */}
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <label htmlFor="create-template-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {titleLabel ?? t('pages.passwords.templateModal.titleLabel', 'Başlık')}
          </label>
          {titleError && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-2" role="alert">
              {titleError}
            </p>
          )}
          <input
            type="text"
            id="create-template-title"
            value={templateTitle}
            onChange={(e) => {
              setTemplateTitle(e.target.value);
              setTitleError(null);
            }}
            className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow"
            placeholder={titlePlaceholder ?? t('pages.passwords.templateModal.titlePlaceholder', 'Şablon adı')}
            autoFocus
          />
        </div>

        {/* İçerik alanı - EditPasswordForm not alanı ile aynı */}
        <div className="flex-1 overflow-hidden flex flex-col px-6 py-4 min-h-0">
          <label htmlFor="create-template-content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {contentLabel ?? t('pages.passwords.templateModal.fieldsLabel', 'İçerik')}
          </label>
          {contentError && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-2 flex-shrink-0" role="alert">
              {contentError}
            </p>
          )}
          <NoteFieldWithContextMenu
            id="create-template-content"
            value={content}
            onChange={(v) => {
              setContent(v);
              setContentError(null);
            }}
            rows={22}
            wrapperClassName="flex-1 min-h-0"
            className="w-full flex-1 min-h-[480px] px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow resize-y"
            placeholder={contentPlaceholder ?? t('pages.passwords.templateModal.fieldPlaceholder', 'Markdown ile yazın: tablo, kalın, liste...')}
            singleViewMode
          />
        </div>

        {/* Footer - EditPasswordForm ile aynı */}
        <div className="px-6 py-4 bg-gray-50/80 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700/80 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors font-medium"
          >
            {cancelLabel ?? t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!templateTitle.trim() || !content.trim() || isSaving}
            className="px-5 py-2.5 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity font-medium shadow-lg shadow-primary/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" />
                {t('common.loading', 'Yükleniyor...')}
              </>
            ) : (
              saveLabel ?? t('common.save')
            )}
          </button>
        </div>
      </form>
    </AddPasswordModal>
  );
};

export default CreateTemplateModal;
